import React, {FC, useEffect, useMemo} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router-dom'

import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import {Theme} from '@mui/material/styles'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {FileInput} from '@progyny/components/Buttons/FileInput'
import {useClaimTreatmentsQuery} from '@progyny/generated/graphql'
import {useDocumentTypes} from '@progyny/hooks/useDocumentTypes'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {ReimbursementMobileStepper} from '@progyny/pages/Budget/Reimbursement/ReimbursementMobileStepper'
import ExistingMemberID from '@progyny/pages/Budget/Reimbursement/helpers/ExistingMemberID'
import FileInfoList, {
  calculateTotalSize,
} from '@progyny/pages/Budget/Reimbursement/helpers/FileInfoList'
import {RemoteFilePreview} from '@progyny/pages/Budget/Reimbursement/helpers/RemoteFilePreview'
import {returnDescriptionPlaceholder} from '@progyny/pages/Budget/Reimbursement/helpers/util'
import {useClaimStepMeta} from '@progyny/pages/Budget/hook/useClaimStepMeta'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {
  ExistingClaimDocument,
  useClaim,
  useClaimDocuments,
  useClaimTreatment,
} from '@progyny/store/claims'
import {useMeStore} from '@progyny/store/meStore'

import {theme} from '@root/theme'

type RequestProps = RouteComponentProps & {
  activeStep: number
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
  // True while the "Upload additional documents" direct entry is still resolving
  // the last submitted claim. Used to defer the missing-claim redirect so we do
  // not bounce the member to the claim flow before the claimant is seeded.
  isResolvingDirectEntry?: boolean
}
const useStyles = makeStyles<{isDownSM: boolean}>()(
  (theme: Theme, {isDownSM}) => ({
    textAreaField: {
      textarea: {
        height: `${26}px !important`,
        transition: 'height 0.25s ease-in',
        '&:focus': {
          height: `${106}px !important`,
        },
      },
    },
    documentChecklist: {
      borderRadius: 4,
      backgroundColor: theme.palette.other.info,
      ul: {
        marginBottom: 0,
        paddingLeft: theme.spacing(3),
        li: {
          paddingBottom: theme.spacing(2),
        },
      },
    },
    customTooltip: {
      padding: 0,
      borderRadius: 2,
      backgroundColor: theme.palette.background.default,
      maxWidth: '100%',
      border: `1px solid ${theme.palette.error.main}`,
    },
    idUploadHeader: {
      display: 'flex',
      flexDirection: isDownSM ? 'column' : 'row',
      alignItems: isDownSM ? 'flex-start' : 'end',
      justifyContent: 'space-between',
      gap: isDownSM ? 8 : 0,
      marginBottom: isDownSM ? 8 : 16,
    },
  })
)
export const VALID_MIMETYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'image/heic',
  'image/heif',
]

export const FileMax = 20 * 1024 * 1024
const TotalMax = 500 * 1024 * 1024

export const Documents: FC<RequestProps> = (props) => {
  const {activeStep, setActiveStep, history, match, isResolvingDirectEntry} =
    props
  const {t} = useTranslation()
  const isDownSM = useResponsive('down', 'sm')
  const {classes} = useStyles({isDownSM})
  const {
    documents,
    setDocuments,
    docType,
    setDocType,
    documentDescription,
    setDocumentDescription,
    idDocuments,
    setIdDocuments,
    docIdType,
    setDocIdType,
    isConsetSelected,
    setConsetSelected,
    isMemberIDRemoved,
    setMemberIDRemoved,
    existingDocuments,
    setExistingDocuments,
  } = useClaimDocuments()
  const {claimRequested} = useClaim()
  const {treatment, treatmentLabel, setTreatment, setTreatmentLabel} =
    useClaimTreatment()
  const {stepEnumList, isMedicallyNecessaryRelevant} =
    useClaimStepMeta(claimRequested)

  // When the user lands here directly via "Upload additional documents" (without
  // visiting the Treatment step), the treatment label is not yet set in context.
  // The seeded treatment (treatmentName) may be either the value/slug or its
  // display label, so resolve it from claimTreatments and persist both the
  // canonical value and the label so the Overview step can show it.
  const {data: claimTreatmentsData} = useClaimTreatmentsQuery({
    skip: !treatment || !!treatmentLabel,
  })
  useEffect(() => {
    if (treatment && !treatmentLabel) {
      const matched = claimTreatmentsData?.claimTreatments?.find(
        (claimTreatment) =>
          claimTreatment?.value === treatment ||
          claimTreatment?.label?.toLowerCase() === treatment.toLowerCase()
      )
      if (matched?.label) {
        if (matched.value && matched.value !== treatment) {
          setTreatment(matched.value)
        }
        setTreatmentLabel(matched.label)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimTreatmentsData, treatment, treatmentLabel])
  const fileCount = useMemo<number>(() => documents.length, [documents])
  // Existing (already submitted) documents also count towards the minimum of
  // one document required to proceed.
  const hasAnyDocument = useMemo<boolean>(
    () => documents.length > 0 || existingDocuments.length > 0,
    [documents, existingDocuments]
  )
  const totalFileSize = useMemo<number>(
    () => calculateTotalSize(documents),
    [documents]
  )
  const user = useMeStore((s) => s.user)
  const {getPath} = useRoutes()
  const {documentTypes} = useDocumentTypes()
  const parentRoute = match.url.split('/').slice(0, -1).join('/')
  const identityDocumentUrl = user?.member?.identityDocumentUrl

  const hasFileExceededMaxSize = useMemo<boolean>(
    () => documents.some((doc) => doc.file.size > FileMax),
    [documents]
  )

  const isDisabled =
    !hasAnyDocument ||
    totalFileSize > TotalMax ||
    hasFileExceededMaxSize ||
    !isConsetSelected
  const isFileUploadDisabled = !docType || !documentDescription
  const isIdFileUploadDisabled = !docIdType || idDocuments.length > 0

  function onIDFileInputChange(event: React.FormEvent) {
    const target = event.target as HTMLInputElement
    const file = target.files![0]
    if (file && VALID_MIMETYPES.includes(file.type)) {
      setIdDocuments([{file, docType: docIdType}])
      setDocIdType('')
    }
  }

  function onFileInputChange(event: React.FormEvent) {
    const target = event.target as HTMLInputElement
    const file = target.files![0]
    target.value = ''
    if (file && VALID_MIMETYPES.includes(file.type)) {
      setDocuments([
        ...documents,
        {file: file, docType: docType, description: documentDescription},
      ])
      resetForm()
    }
  }

  useEffect(() => {
    // Wait until the last submitted claim lookup has resolved before deciding
    // there is no claim. Otherwise a direct "Upload additional documents" entry
    // would redirect to the claim flow during the async seeding window.
    if (isResolvingDirectEntry) return
    if (!claimRequested) {
      history.push(`${getPath(RouteEnum.BUDGET)}/reimbursement/claim`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimRequested, isResolvingDirectEntry])

  function resetForm() {
    setDocType('')
    setDocumentDescription('')
  }

  function removeFile(file: File) {
    setDocuments(documents.filter((item) => item.file !== file))
  }

  const removeIdDocument = (file: File) => {
    setIdDocuments(idDocuments.filter((item) => item.file !== file))
  }

  const removeExistingDocument = (document: ExistingClaimDocument) => {
    setExistingDocuments(
      existingDocuments.filter((item) => item.id !== document.id)
    )
  }

  function handleSubmit() {
    if (isMedicallyNecessaryRelevant) {
      history.push(`${parentRoute}/medical-information`)
      return
    }
    if (user?.member?.contract?.reimbursementMode === 'bank_transfer') {
      history.push(`${parentRoute}/payments`)
    } else {
      history.push(`${parentRoute}/overview`)
    }
  }

  useEffect(() => {
    // Find the index of DOCUMENTS step in the step list
    const documentsStepIndex = stepEnumList.findIndex(
      (step) => step === 'DOCUMENTS'
    )
    if (documentsStepIndex !== -1) {
      setActiveStep(documentsStepIndex)
    }
  }, [setActiveStep, stepEnumList])

  const isCheckboxDisabled =
    !hasAnyDocument ||
    (isMemberIDRemoved || identityDocumentUrl === null
      ? idDocuments?.length === 0
      : identityDocumentUrl === null)

  useEffect(() => {
    const shouldUncheck =
      !hasAnyDocument ||
      (isMemberIDRemoved || identityDocumentUrl === null
        ? idDocuments?.length === 0
        : identityDocumentUrl === null)
    if (shouldUncheck && isConsetSelected) {
      setConsetSelected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAnyDocument,
    idDocuments,
    identityDocumentUrl,
    isConsetSelected,
    isMemberIDRemoved,
  ])

  return (
    <>
      <Grid
        container
        mt={2}
        spacing={isDownSM ? 0 : 3}
        width={{xs: '100%', md: '85%'}}
      >
        <Grid item xs={12} md={9}>
          <Box display={'flex'} flexDirection={'column'}>
            <Box className={classes.idUploadHeader}>
              <Typography variant={'h6'} mb={{xs: 0}} mt={{xs: 4, sm: 2}}>
                {t('budget_reimbursement_documents_upload_your_id_document')}
              </Typography>
              <Tooltip
                title={
                  <Paper sx={{width: 565, p: 2}}>
                    <Typography fontSize={18} fontWeight={600}>
                      {t(
                        'budget_reimbursement_documents_please_upload_valid_governmentissued_id'
                      )}
                    </Typography>
                    <Typography
                      color={theme.palette.other.brandColors.lightGray}
                      mt={2}
                      fontSize={16}
                      fontWeight={400}
                    >
                      {t(
                        'budget_reimbursement_documents_acceptable_forms_of_id_may'
                      )}
                    </Typography>
                    <Typography
                      color={theme.palette.other.brandColors.lightGray}
                      mt={1}
                      fontSize={16}
                      fontWeight={400}
                    >
                      {t(
                        'budget_reimbursement_documents_for_example_some_countries_accept'
                      )}
                    </Typography>
                    <Typography
                      color={theme.palette.other.brandColors.lightGray}
                      mt={1}
                      fontSize={16}
                      fontWeight={400}
                    >
                      {t(
                        'budget_reimbursement_documents_make_sure_document_you_provide'
                      )}
                    </Typography>
                  </Paper>
                }
                placement='top'
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'transparent',
                      padding: 0,
                      boxShadow: 'none',
                    },
                  },
                }}
              >
                <Typography
                  variant={'caption'}
                  data-testid={'file-name'}
                  display={'flex'}
                  alignItems={'center'}
                  gap={1}
                  sx={{cursor: 'help'}}
                >
                  <InfoOutlinedIcon
                    sx={{color: theme.palette.other.brandColors.lightGray}}
                  />
                  {t(
                    'budget_reimbursement_documents_what_documents_are_accepted'
                  )}
                </Typography>
              </Tooltip>
            </Box>
            <Grid container spacing={isDownSM ? 0 : 2}>
              <Grid item xs={12} md={12}>
                <FormControl
                  variant={'outlined'}
                  fullWidth
                  classes={{root: classes.textAreaField}}
                >
                  <TextField
                    required
                    fullWidth
                    sx={{
                      backgroundColor:
                        idDocuments?.length > 0 ||
                        (!isMemberIDRemoved && !!identityDocumentUrl)
                          ? theme.palette.other.brandColors.disabledGray
                          : theme.palette.other.brandColors.white,
                    }}
                    disabled={
                      idDocuments?.length > 0 ||
                      (!isMemberIDRemoved && !!identityDocumentUrl)
                    }
                    inputProps={{
                      maxLength: 200,
                      'data-testid': 'textarea_doc_type',
                    }}
                    name='document_type'
                    label={t('budget_reimbursement_documents_document_type')}
                    placeholder={t(
                      'budget_partner_agreement_pa_form_for_example_passport'
                    )}
                    value={docIdType}
                    onChange={(event) => setDocIdType(event.target.value)}
                    margin={'dense'}
                    variant='outlined'
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box
              mt={{xs: 2, sm: 3}}
              mb={{xs: 2, sm: 1}}
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'flex-start'}
            >
              <FileInput
                onChange={onIDFileInputChange}
                startIcon={<ArrowUpwardSharpIcon />}
                accept={VALID_MIMETYPES.join(',')}
                disabled={isIdFileUploadDisabled}
                size={'large'}
                color={'primary'}
                id={'file-id-input'}
              >
                {t('budget_reimbursement_documents_choose_file')}
              </FileInput>
            </Box>
            {!isMemberIDRemoved && !!identityDocumentUrl ? (
              <ExistingMemberID setMemberIDRemoved={setMemberIDRemoved} />
            ) : (
              <Box
                width={'100%'}
                height={'100%'}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
              >
                {idDocuments.length > 0 && (
                  <FileInfoList
                    documents={idDocuments!}
                    removeFile={removeIdDocument}
                    hideFileSize={true}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box display={'flex'} flexDirection={'column'} mt={7}>
            <Box mb={{xs: 1, sm: 2}}>
              <Typography variant={'h6'} mb={{xs: 0}} mt={{xs: 4, sm: 2}}>
                {t(
                  'budget_reimbursement_documents_upload_all_your_treatment_documents'
                )}
              </Typography>
              <PGTypography variant='body'>
                {t(
                  'budget_reimbursement_documents_please_upload_all_document_types'
                )}
              </PGTypography>
            </Box>
            <Grid container spacing={isDownSM ? 0 : 2}>
              <Grid item xs={12} md={4}>
                <FormControl variant={'outlined'} margin={'dense'} fullWidth>
                  <InputLabel id={'document-select'} required>
                    {t('budget_reimbursement_documents_document_type')}
                  </InputLabel>
                  <Select
                    required
                    labelId={'document-select'}
                    size='medium'
                    name='type'
                    margin={'dense'}
                    label={t('budget_reimbursement_documents_document_type')}
                    placeholder={t(
                      'budget_reimbursement_documents_select_type'
                    )}
                    value={docType}
                    onChange={(event) => setDocType(event.target.value)}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    {documentTypes?.map((docType) => (
                      <MenuItem value={docType.value} key={docType.value}>
                        {t(docType.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl
                  variant={'outlined'}
                  fullWidth
                  classes={{root: classes.textAreaField}}
                >
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    inputProps={{
                      maxLength: 200,
                      'data-testid': 'textarea_desc',
                    }}
                    name='message'
                    label={t(
                      'budget_history_helper_render_documents_description'
                    )}
                    placeholder={returnDescriptionPlaceholder(docType, t)}
                    value={documentDescription}
                    onChange={(event) =>
                      setDocumentDescription(event.target.value)
                    }
                    margin={'dense'}
                    variant='outlined'
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box
              mt={{xs: 2, sm: 3}}
              mb={{xs: 2, sm: 1}}
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'flex-start'}
            >
              <FileInput
                onChange={onFileInputChange}
                startIcon={<ArrowUpwardSharpIcon />}
                accept={VALID_MIMETYPES.join(',')}
                disabled={isFileUploadDisabled}
                size={'large'}
                color={'primary'}
                id={'file-input'}
              >
                {t('budget_reimbursement_documents_choose_file')}
              </FileInput>
            </Box>
            <Box
              width={'100%'}
              height={'100%'}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'flex-start'}
            >
              {existingDocuments.map((existingDocument) => (
                <RemoteFilePreview
                  key={existingDocument.id}
                  document={existingDocument}
                  removeDocument={removeExistingDocument}
                />
              ))}
              {fileCount > 0 && (
                <FileInfoList documents={documents} removeFile={removeFile} />
              )}
            </Box>
            <Box mt={7} position='relative'>
              <FormGroup>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      color: 'error.main',
                      position: 'absolute',
                      top: -5,
                      left: 20,
                      fontSize: 20,
                      lineHeight: 1,
                    }}
                  >
                    *
                  </Box>

                  <FormControlLabel
                    sx={{
                      alignItems: 'flex-start',
                      '& .MuiCheckbox-root': {pt: 0},
                    }}
                    disabled={isCheckboxDisabled}
                    control={
                      <Checkbox
                        checked={isConsetSelected}
                        disableRipple
                        onChange={(e) => setConsetSelected(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{fontSize: 16, fontWeight: 500}}>
                        <Trans i18nKey='budget_reimbursement_documents_i_confirm_that_i_have' />
                      </Box>
                    }
                  />
                </Box>
              </FormGroup>
            </Box>
          </Box>
          <ReimbursementMobileStepper
            isDisabled={isDisabled}
            activeStep={activeStep}
            stepNumber={stepEnumList.length}
            toCancelPath={`${getPath(RouteEnum.BUDGET)}/reimbursement/treatment`}
            toContinuePath={handleSubmit}
            cancelButtonText={t('budget_reimbursement_documents_go_back')}
            continueButtonText={t('common_next')}
            continueButtonTooltip={
              hasFileExceededMaxSize ? (
                <Alert severity='error'>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_please_remove_files_that_exceed'
                    )}
                  </Typography>
                </Alert>
              ) : undefined
            }
            continueButtonTooltipClassName={classes.customTooltip}
            desktopMarginTop={8}
            desktopJustifyContent='end'
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
          >
            <Box py={3} px={2} className={classes.documentChecklist}>
              <Typography variant={'h5'} gutterBottom>
                {t('budget_reimbursement_documents_document_checklist')}
              </Typography>
              <ul>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_itemised_invoice_showing_services_or'
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_proof_of_payment_if_invoice'
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_medical_prescription_for_any_medication'
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_medical_referral_if_service_was'
                    )}
                  </Typography>
                </li>
              </ul>
              <Typography variant={'h5'} gutterBottom>
                {t('budget_reimbursement_documents_file_format_tips')}
              </Typography>
              <ul>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_please_add_files_in_pdf'
                    )}
                  </Typography>
                </li>

                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_please_upload_each_document_individuall'
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant={'caption'}>
                    {t(
                      'budget_reimbursement_documents_please_attach_your_treatment_plans'
                    )}
                  </Typography>
                </li>
              </ul>
            </Box>
            <Box mt={2}>
              <Alert
                severity='info'
                sx={{backgroundColor: theme.palette.other.info}}
              >
                <Typography variant={'caption'}>
                  {t(
                    'budget_reimbursement_documents_we_share_only_absolute_minimum'
                  )}
                </Typography>
              </Alert>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
